<template>
  <div>
    <!-- Device Status Indicator -->
    <div v-if="deviceChecking" class="device-status-banner">
      <div class="status-content">
        <div class="status-spinner"></div>
        <span>Connecting to device...</span>
      </div>
    </div>
    <div v-else-if="!isDeviceOnline" class="device-status-banner offline">
      <div class="status-content">
        <font-awesome-icon :icon="['fas', 'exclamation-triangle']" class="status-icon" />
        <span>Device offline - device management unavailable</span>
      </div>
    </div>

    <!-- Schedule & Block Buttons -->
    <div class="button‐group">
      <button class="btn‐schedule" @click="openModal('visibleScheduleDowntime')" :disabled="!isDeviceOnline">
        <h6>Schedule Downtime</h6>
      </button>
      <button class="pageButton" @click="openModal('visibleQuickBlock')" :disabled="!isDeviceOnline">
        <h6>Quick Block</h6>
      </button>
    </div>

    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleDeviceBlocking"
          :aria-expanded="deviceBlockingOpen"
      >
        <font-awesome-icon
            :icon="['fas', deviceBlockingOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Devices</h6>
    </BannerComponent>

    <div v-if="deviceBlockingOpen">
      <!-- Search & Filter -->
      <div class="search-bar">
        <div class="search-input-wrapper">
          <font-awesome-icon :icon="['fas','magnifying-glass']" class="search-icon"/>
          <input
              type="text"
              v-model="deviceSearchTerm"
              placeholder="Search Devices..."
              class="search-input cardBackground"
              @input="goToDevicePage(1)"
          />
        </div>
<!--        <button class="filter-btn" @click="filter">
          <font-awesome-icon :icon="['fas','filter']" />
        </button>-->
      </div>

      <!-- Devices List (paginated) -->
      <div class="list">
        <div
            v-for="device in pagedDevices"
            :key="device.ip"
            class="list__item clickable-device-row"
            @click="openDeviceInfoModal(device)"
        >

          <!-- 1) Device icon -->
          <div class="device‐column device‐icon">
            <font-awesome-icon :icon="['fas','mobile-screen-button']" />
          </div>

          <!-- 2) Device name -->
          <div class="device‐column device‐name">
            {{ getDeviceName(device) }}
          </div>

          <!-- 3) Device status -->
          <div class="device‐column device‐status">
            {{ displayStatus(device.blocked) }}
          </div>

          <!-- 4) Info icon -->
          <div class="device‐column device‐info‐icon">
            <font-awesome-icon
              :icon="['fas','info-circle']"
              :class="device.blocked ? 'info-icon-blocked' : 'info-icon-unblocked'"
            />
          </div>
        </div>

        <!-- If there are no devices to show: -->
        <div v-if="pagedDevices.length === 0" class="no-results">
          No devices found
        </div>

      </div>

      <!-- Pagination Controls -->
      <pagination-control
          :currentPage="currentDevicesPage"
          :pageCount="devicesPageCount"
          :maxVisible="3"
          @update:currentPage="goToDevicePage"
      />
    </div>

    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleGroupBlocking"
          :aria-expanded="groupBlockingOpen"
      >
        <font-awesome-icon
            :icon="['fas', groupBlockingOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Groups</h6>
    </BannerComponent>

    <div v-if="groupBlockingOpen && !groupsInactive">
      <!-- “Add Group” button -->
      <div class="add-group-button">
        <button class="pageButton" @click="openCreateGroupModal('Add')">
          <h6>Add Group</h6>
        </button>
      </div>

      <!-- Search bar for groups -->
      <div class="search-bar" style="margin-top: 0.75rem;">
        <div class="search-input-wrapper">
          <font-awesome-icon
              :icon="['fas','magnifying-glass']"
              class="search-icon"
          />
          <input
              type="text"
              v-model="groupSearchTerm"
              placeholder="Search Groups..."
              class="search-input cardBackground"
              @input="goToGroupPage(1)"
          />
        </div>
<!--        <button class="filter-btn" @click="/* optional filter action */">
          <font-awesome-icon :icon="['fas','filter']" />
        </button>-->
      </div>

      <!-- List of groups with checkbox toggles -->
      <div class="list">
        <div
            v-for="group in pagedGroups"
            :key="group.name"
            class="list__item"
            style="display: flex; align-items: center; padding: 0.5rem; border-bottom: 1px solid var(--color-border-input);"
        >

          <!-- Group name -->
          <div class="group-name">{{ group.name }} </div>

          <!-- Info icon -->
          <div class="device‐column device‐info‐icon" @click="openBlockGroupMode(group)">
            <font-awesome-icon :icon="['fas','info-circle']" style="margin-right: 1rem;" />
          </div>
        </div>

        <!-- If no groups match the search -->
        <div v-if="filteredGroups.length === 0" class="no-results" style="padding: 0.75rem; text-align: center; color: var(--text-primary);">
          No groups found
        </div>
      </div>

      <!-- GROUP pagination controls -->
      <div class="pagination-controls" style="margin-top: 0.5rem;">
        <!-- “<” Previous page button -->
        <button
            class="page-arrow"
            :disabled="currentGroupPage === 1"
            @click="goToGroupPage(currentGroupPage - 1)"
        >
          <font-awesome-icon :icon="['fas','fa-angles-left']" />
        </button>

        <!-- Individual page numbers -->
        <button
            v-for="page in visibleGroupPages"
            :key="page"
            class="page-number"
            :class="{ 'active-page': page === currentGroupPage }"
            @click="goToGroupPage(page)"
        >
          {{ page }}
        </button>

        <!-- “>” Next page button -->
        <button
            class="page-arrow"
            :disabled="currentGroupPage === groupPageCount"
            @click="goToGroupPage(currentGroupPage + 1)"
        >
          <font-awesome-icon :icon="['fas','fa-angles-right']" />
        </button>
      </div>

    </div>

    <div v-if="groupBlockingOpen && groupsInactive">
      <h2 style="margin: 2rem;">Groups Coming Soon!</h2>
    </div>

    <BannerComponent>
      <button
          class="banner-toggle-btn"
          @click="toggleSchedule"
          :aria-expanded="scheduleOpen"
      >
        <font-awesome-icon
            :icon="['fas', scheduleOpen ? 'chevron-down' : 'chevron-right']"
        />
      </button>
      <h6 class="banner-heading">Scheduled Downtime</h6>
    </BannerComponent>

    <div v-if="scheduleOpen">
      <div class="list">
        <div v-if="rules.length === 0" class="no-results">
          No scheduled rules found
        </div>
        <div v-for="rule in pagedRules"
             :key="rule.id"
             class="list__item"
             style="color: var(--text-primary)"
          >

          <div v-if="rule.frequency==='daily'">
            <div v-if="rule.hosts.length === 1" class="schedule-device">
              <font-awesome-icon :icon="['fas','mobile-screen-button']" /> {{getDeviceName(getDeviceByHostIdentifier(rule.hosts[0]))}}
            </div>
            <div v-else class="schedule-device">
              <font-awesome-icon :icon="['fas','mobile-screen-button']" /> {{rule.hosts.length}} devices
            </div>
            <div class="frequency">Daily</div>
            <div class="times">
              {{militaryToAmPm(rule.start_time)}} ⮕ {{militaryToAmPm(rule.end_time)}}
            </div>
          </div>

          <div v-else-if="rule.frequency==='weekly'">
            <div v-if="rule.hosts.length === 1" class="schedule-device">
              <font-awesome-icon :icon="['fas','mobile-screen-button']" /> {{getDeviceName(getDeviceByHostIdentifier(rule.hosts[0]))}}
            </div>
            <div v-else class="schedule-device">
              <font-awesome-icon :icon="['fas','mobile-screen-button']" /> {{rule.hosts.length}} devices
            </div>
            <div class="frequency">{{scheduleDays[rule.day].name}}s</div>
            <div class="times">
              {{militaryToAmPm(rule.start_time)}} ⮕ {{militaryToAmPm(rule.end_time)}}
            </div>
          </div>

          <div v-else-if="rule.frequency==='monthly'">
            <div v-if="rule.hosts.length === 1" class="schedule-device">
              <font-awesome-icon :icon="['fas','mobile-screen-button']" /> {{getDeviceName(getDeviceByHostIdentifier(rule.hosts[0]))}}
            </div>
            <div v-else class="schedule-device">
              <font-awesome-icon :icon="['fas','mobile-screen-button']" /> {{rule.hosts.length}} devices
            </div>
            <div class="frequency">Monthly Day {{rule.date}}</div>
            <div class="frequency">
                {{militaryToAmPm(rule.start_time)}} ⮕ {{militaryToAmPm(rule.end_time)}}
            </div>
          </div>

          <div v-else>
            <div v-if="rule.hosts.length === 1" class="schedule-device">
              <font-awesome-icon :icon="['fas','mobile-screen-button']" /> {{getDeviceName(getDeviceByHostIdentifier(rule.hosts[0]))}}
            </div>
            <div v-else class="schedule-device">
              <font-awesome-icon :icon="['fas','mobile-screen-button']" /> {{rule.hosts.length}} devices
            </div>
            <div class="frequency">
              {{parseDate(rule.start_time.split('T')[0])}} {{militaryToAmPm(rule.start_time.split('T')[1])}}
            </div>
            <div class="frequency">
              ⮕ {{parseDate(rule.end_time.split('T')[0])}} {{militaryToAmPm(rule.end_time.split('T')[1])}}
            </div>
          </div>

          <div class="icon-group">
            <font-awesome-icon :icon="['fas','trash']" class="icon-trash" @click="deleteRuleButton(rule)"/>
            <font-awesome-icon :icon="['fas','copy']" class="icon-duplicate" @click="duplicateRule(rule)"/>
            <font-awesome-icon :icon="['fas','info-circle']" class="icon-info" @click="openRuleInfo(rule)"/>
          </div>

        </div>
      </div>
      <pagination-control
          :currentPage="currentRulesPage"
          :pageCount="rulesPageCount"
          :maxVisible="3"
          @update:currentPage="goToRulesPage"
      />
    </div>

    <!-- Schedule Downtime Modal -->
    <BaseModal
        :visible="visibleScheduleDowntime"
        title="Schedule Downtime"
        @close="closeScheduleDowntimeModal()"
        @submit="scheduleDowntime()"
        submitLabel="Schedule"
        :cancelShow=false
        modalWidth="20rem"
    >
      <!-- Select Groups -->
      <div v-if="!groupsInactive">
        <div class="text-box-label">
          Groups:
        </div>
        <CustomSelect
            v-model="selectedGroups"
            :options="groupsToSelect"
            :multiSelect="true"
            :show-select-all="true"
            :searchable="false"
            placeholder="Select groups..."
        />
      </div>

      <!-- Select Devices -->
      <div class="text-box-label" style="margin-top:.5rem;">
        Devices:
      </div>
      <CustomSelect
          v-model="selectedDevices"
          :options="devicesNotInSelectedGroupsToSelect"
          :multiSelect="true"
          :show-select-all="true"
          :searchable="false"
          placeholder="Select devices..."
      />

      <div class="text-box-label" style="margin-top:.5rem;">
        When:
      </div>
      <CustomSelect
          v-model="scheduleFrequency"
          :options="scheduleFrequencies"
          placeholder="Daily"
          :searchable="false"
      />

      <!-- Weekly Options -->
      <div v-if="scheduleFrequency === 1">
        <div class="text-box-label" style="margin-top:.5rem;">
          Select Day of Week:
        </div>
        <CustomSelect
            v-model="scheduleDay"
            :options="scheduleDays"
            placeholder="Daily"
            :searchable="false"
        />
      </div>

      <!-- Monthly Options -->
      <div v-if="scheduleFrequency === 2">
        <div class="text-box-label" style="margin-top:.5rem;">
          Select Day of Month:
        </div>
        <CustomSelect
            v-model="dayOfMonth"
            :options="daysOfMonth"
            placeholder="0"
            :searchable="false"
        />
      </div>

      <!-- Custom Options -->
      <div v-if="scheduleFrequency === 3">
        <div class="text-box-label" style="margin-top:.5rem;">
          Start Date:
        </div>
        <div class="text-box">
          <!-- Use an <input type="time"> bound to `startTime` -->
          <input
              type="date"
              class="text-box-entry"
              v-model="startDate"
              style="border: none; background: transparent; padding: 0.15rem; width: 100%; margin-left: 1rem;"
          />
        </div>

        <div class="text-box-label" style="margin-top:.5rem;">
          End Date:
        </div>
        <div class="text-box">
          <!-- Use an <input type="time"> bound to `startTime` -->
          <input
              type="date"
              class="text-box-entry"
              v-model="endDate"
              style="border: none; background: transparent; padding: 0.15rem; width: 100%; margin-left: 1rem;"
          />
        </div>
      </div>

      <div class="text-box-label" style="margin-top:.5rem;">
        Start Time:
      </div>
      <div class="text-box">
        <!-- Use an <input type="time"> bound to `startTime` -->
        <input
            type="time"
            class="text-box-entry"
            v-model="startTime"
            style="border: none; background: transparent; padding: 0.15rem; width: 100%; margin-left: 1rem;"
        />
      </div>

      <div class="text-box-label" style="margin-top:.5rem;">
        End Time:
      </div>
      <div class="text-box">
        <!-- Use an <input type="time"> bound to `endTime` -->
        <input
            type="time"
            class="text-box-entry"
            v-model="endTime"
            style="border: none; background: transparent; padding: 0.15rem; width: 100%; margin-left: 1rem;"
        />
      </div>

      <div v-if="showAllSelectedDevices">
        <!-- All Selected Devices -->
        <div class="text-box-label" style="margin-top:.5rem;">
          Devices to be Scheduled:
        </div>
        <div class="selected-devices-list">
          <div
              v-for="mac in combinedDevices"
              :key="mac"
              class="selected-device-item"
          >

            <!-- Device Name -->
            {{ getDeviceName(getDeviceByHostIdentifier(mac)) }}

          </div>
        </div>
        <div v-if="combinedDevices.length === 0" class="selected-device-item">
          No devices selected
        </div>
      </div>

    </BaseModal>

    <!-- Quick Block Modal -->
    <BaseModal
        :visible="visibleQuickBlock"
        title="Quick Block"
        @close="closeQuickBlockModal()"
        @submit="submitQuickBlock()"
        submitLabel="Block Internet"
        submitWidth="10rem"
        :cancelShow=false
    >

      <!-- Button bar: four durations -->
      <div class="text-box-label" style="margin-top: .5rem;">
        Block Duration:
      </div>
      <div class="button-bar">
        <button
            v-for="opt in blockDurations"
            :key="opt.value"
            type="button"
            @click="selectedDuration = opt.value"
            :class="['block-btn', { 'block-btn-active': selectedDuration === opt.value }]"
            style="font-family: 'Poppins', 'Inter', sans-serif; font-size: 16px; font-weight: 300; padding: 0 .48rem;"
        >
          {{ opt.label }}
        </button>
      </div>

      <div v-if="!groupsInactive">
        <!-- Select Groups -->
        <div class="text-box-label">
          Groups:
        </div>
        <CustomSelect
            v-model="selectedGroups"
            :options="groupsToSelect"
            :multiSelect="true"
            :show-select-all="true"
            :searchable="false"
            placeholder="Select groups..."
        />
      </div>

      <!-- Select Devices -->
      <div class="text-box-label" style="margin-top:.5rem;">
        Devices:
      </div>
      <CustomSelect
          v-model="selectedDevices"
          :options="devicesNotInSelectedGroupsToSelect"
          :multiSelect="true"
          :show-select-all="true"
          :searchable="false"
          placeholder="Select devices..."
          ref="deviceSelect"
          @open="onDropdownOpen"
          @close="onDropdownClose"
      />
      <div
          class="dropdownSpacer"
          v-if="dropdownOpen && visibleQuickBlock"
      ></div>

    </BaseModal>

    <!-- Unblock Modal -->
    <BaseModal
        :visible="visibleUnblockModal"
        title="Unblock Devices"
        @close="closeUnblockModal()"
        @submit="submitUnblock()"
        submitLabel="Unblock"
        submitWidth="7rem"
        :cancelShow=false
    >
      <CustomSelect
          v-model="selectedDevices"
          :options="blockedDevicesToSelect"
          :multiSelect="true"
          :show-select-all="true"
          :searchable="false"
          placeholder="Select devices..."
      />
    </BaseModal>

    <!-- Device Info Modal -->
    <BaseModal
        :visible="visibleDeviceInfo"
        title="Device Info"
        @close="closeDeviceInfoModal()"
        @cancel="closeDeviceInfoModal()"
        @submit="deviceInfoSubmit()"
        :submitLabel="deviceInfoEditMode ? 'Save' : device.blocked ? 'Unblock' : 'Block Internet'"
        :submit-width="deviceInfoEditMode ? '6rem' : device.blocked ? '7rem' : '10rem'"
        :cancelShow="deviceInfoEditMode"
    >
      <!-- read‐only view -->
      <div class="device-info text-box" v-if="!deviceInfoEditMode">
        <div class="text-box-title">{{getDeviceName(this.device)}}</div>
        <div class ="pageButton rename cardBackground" @click="toEditMode()">
          Rename
        </div>
      </div>

      <!-- edit-mode -->
      <div class="text-box" v-if="deviceInfoEditMode">
        <input
            type="text"
            class="fieldInput text-box-entry"
            v-model="newDeviceName"
            :placeholder="getDeviceName(this.device)"
            style="font-size: .9rem; font-family: 'Poppins', 'Inter', sans-serif; padding: 0; border: none; box-shadow: none; margin-bottom: .4rem; margin-top: .25rem;"
        />
        </div>

      <div class="text-box">
        <div class="text-box-title">Status:</div>
        <div class="text-box-entry">{{ displayStatus(device.blocked) }}</div>
      </div>

      <div class="text-box">
        <div class="text-box-title IP">IP:</div>
        <div class="text-box-entry">{{ device.ip }}</div>
      </div>

      <div class="text-box">
        <div class="text-box-title MAC">MAC:</div>
        <div class="text-box-entry">{{ device.mac }}</div>
      </div>

    </BaseModal>

    <!-- Create Group Modal -->
    <BaseModal
        :visible="visibleCreateGroup"
        :title="createGroupScope === 'Block Internet' ? 'Manage Group' : 'Create Group'"
        @close="closeCreateGroupModal()"
        @submit="submitCreateGroupModal()"
        @secondary="editGroup()"
        @cancel="closeCreateGroupModal()"
        :submitLabel="createGroupScope"
        :submitWidth="createGroupScope === 'Block Internet' ? '9rem' : '6rem'"
        :cancelShow="createGroupScope === 'Confirm'"
        :secondaryShow="createGroupScope === 'Block Internet'"
        secondaryLabel="Edit"
        secondaryWidth="5rem"
    >
      <!-- Adding Group -->
      <div v-if="createGroupScope === 'Add'">
        <div class="text-box-label">
          Group Name:
        </div>
        <div class="text-box">
          <input
              type="text"
              class="fieldInput text-box-entry"
              v-model="newGroupName"
              placeholder="New Group Name"
          />
        </div>

        <div class="text-box-label" style="margin-top: 1rem;">
          Devices:
        </div>
        <CustomSelect
            v-model="selectedDevices"
            :options="devicesToSelect"
            :multiSelect="true"
            :show-select-all="true"
            :searchable="false"
            placeholder="Select devices..."
        />

      </div>

      <!-- Block Internet Mode -->
      <div v-if="createGroupScope === 'Block Internet'">
        <div class="text-box-label">
          Group Name:
        </div>
        <div class="text-box" style="margin-bottom: 0;">
          <div class="text-box-entry">
              {{this.group.name}}
          </div>
        </div>
      </div>

      <!-- Editing Mode -->
      <div v-if="createGroupScope === 'Confirm'">
        <div class="text-box-label">
          Group Name:
        </div>
        <div class="text-box">
          <input
              type="text"
              class="fieldInput text-box-entry"
              v-model="newGroupName"
              :placeholder="this.group.name"
          />
        </div>

        <div class="text-box-label" style="margin-top: 1rem;">
          Devices:
        </div>
        <div v-if="createGroupScope === 'Confirm'">
          <CustomSelect
            v-model="group.members"
            :options="devicesToSelect"
            :multiSelect="true"
            :show-select-all="true"
            :searchable="false"
            placeholder="Select Devices..."
          />
        </div>
      </div>

    </BaseModal>

    <!-- Rule Info Modal -->
    <BaseModal
        :visible="visibleRuleInfo"
        title="Schedule Info"
        @close="closeRuleInfo()"
        @cancel="ruleEditMode ? cancelScheduleEdit() : deleteRule()"
        @secondary="editSchedule()"
        @submit="saveScheduleChanges()"
        :cancelLabel="ruleEditMode ? 'Cancel' : 'Delete'"
        :submitLabel="ruleEditMode ? 'Save' : ''"
        :submitShow="ruleEditMode"
        :cancelShow="true"
        :secondaryShow="!ruleEditMode"
        secondaryLabel="Edit"
        secondaryWidth="5rem"
    >

      <!-- Edit Mode Form -->
      <div v-if="ruleEditMode">
        <!-- Select Devices -->
        <div class="text-box-label" style="margin-top:.5rem;">
          Devices:
        </div>
        <CustomSelect
            v-model="editSelectedDevices"
            :options="devicesToSelect"
            :multiSelect="true"
            :show-select-all="true"
            :searchable="false"
            placeholder="Select devices..."
        />

        <div class="text-box-label" style="margin-top:.5rem;">
          When:
        </div>
        <CustomSelect
            v-model="editScheduleFrequency"
            :options="scheduleFrequencies"
            placeholder="Daily"
            :searchable="false"
        />

        <!-- Weekly Options -->
        <div v-if="editScheduleFrequency === 1">
          <div class="text-box-label" style="margin-top:.5rem;">
            Select Day of Week:
          </div>
          <CustomSelect
              v-model="editScheduleDay"
              :options="scheduleDays"
              placeholder="Daily"
              :searchable="false"
          />
        </div>

        <!-- Monthly Options -->
        <div v-if="editScheduleFrequency === 2">
          <div class="text-box-label" style="margin-top:.5rem;">
            Select Day of Month:
          </div>
          <CustomSelect
              v-model="editDayOfMonth"
              :options="daysOfMonth"
              placeholder="0"
              :searchable="false"
          />
        </div>

        <!-- Custom Options -->
        <div v-if="editScheduleFrequency === 3">
          <div class="text-box-label" style="margin-top:.5rem;">
            Start Date:
          </div>
          <div class="text-box">
            <input
                type="date"
                class="text-box-entry"
                v-model="editStartDate"
                style="border: none; background: transparent; padding: 0.15rem; width: 100%; margin-left: 1rem;"
            />
          </div>

          <div class="text-box-label" style="margin-top:.5rem;">
            End Date:
          </div>
          <div class="text-box">
            <input
                type="date"
                class="text-box-entry"
                v-model="editEndDate"
                style="border: none; background: transparent; padding: 0.15rem; width: 100%; margin-left: 1rem;"
            />
          </div>
        </div>

        <div class="text-box-label" style="margin-top:.5rem;">
          Start Time:
        </div>
        <div class="text-box">
          <input
              type="time"
              class="text-box-entry"
              v-model="editStartTime"
              style="border: none; background: transparent; padding: 0.15rem; width: 100%; margin-left: 1rem;"
          />
        </div>

        <div class="text-box-label" style="margin-top:.5rem;">
          End Time:
        </div>
        <div class="text-box">
          <input
              type="time"
              class="text-box-entry"
              v-model="editEndTime"
              style="border: none; background: transparent; padding: 0.15rem; width: 100%; margin-left: 1rem;"
          />
        </div>
      </div>

      <!-- Read-only Mode -->
      <div v-else>

      <div v-if="this.rule.frequency === 'daily'">
        <div class="text-box-label" style="margin-top: .5rem;">
          Frequency:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            Daily
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          Start Time:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{militaryToAmPm(this.rule.start_time)}}
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          End Time:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{militaryToAmPm(this.rule.end_time)}}
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          Devices:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{getDeviceName(getDeviceByHostIdentifier(rule.hosts[0]))}}
          </div>
        </div>
      </div>

      <div v-if="this.rule.frequency === 'weekly'">
        <div class="text-box-label" style="margin-top: .5rem;">
          Frequency:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            Weekly
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          Day of the Week:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{scheduleDays[this.rule.day].name}}
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          Start Time:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{militaryToAmPm(this.rule.start_time)}}
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          End Time:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{militaryToAmPm(this.rule.end_time)}}
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          Devices:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{getDeviceName(getDeviceByHostIdentifier(rule.hosts[0]))}}
          </div>
        </div>

      </div>

      <div v-if="this.rule.frequency === 'monthly'">
        <div class="text-box-label" style="margin-top: .5rem;">
          Frequency:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            Monthly
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          Day of the Month:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{this.rule.date}}
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          Start Time:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{militaryToAmPm(this.rule.start_time)}}
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          End Time:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{militaryToAmPm(this.rule.end_time)}}
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          Devices:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{getDeviceName(getDeviceByHostIdentifier(rule.hosts[0]))}}
          </div>
        </div>
      </div>

      <div v-if="this.rule.frequency === 'custom'">
        <div class="text-box-label" style="margin-top: .5rem;">
          Start Date & Time:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{parseDate(rule.start_time.split('T')[0])}} {{militaryToAmPm(rule.start_time.split('T')[1])}}
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          End Date & Time:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{parseDate(rule.end_time.split('T')[0])}} {{militaryToAmPm(rule.end_time.split('T')[1])}}
          </div>
        </div>

        <div class="text-box-label" style="margin-top: .5rem;">
          Devices:
        </div>
        <div class="text-box">
          <div class="text-box-entry">
            {{getDeviceName(getDeviceByHostIdentifier(rule.hosts[0]))}}
          </div>
        </div>

      </div>

      </div>

    </BaseModal>

    <MessageModal :message="appMessage.text" :type="appMessage.type" ref="messageModal" />
    <LoadingPage :is-visible="loading"/>

  </div>
</template>

<script>
/* eslint-disable */
import MessageModal from "@/components/MessageModal.vue";
import BannerComponent from "@/components/BannerComponent.vue";
import BaseModal from "@/components/BaseModal.vue";
import CustomSelect from "@/components/CustomSelect.vue";
import DeviceAPIService from "@/servicehandlers/DeviceAPIService";
import {useLoginStore} from "@/stores/loginStore";
import LoadingPage from "@/components/LoadingPage.vue";
import PaginationControl from "@/components/PaginationControl.vue";
const deviceAPIService = new DeviceAPIService();

export default {
  name: 'DevicesManagerPage',
  components: {LoadingPage, MessageModal, CustomSelect, BannerComponent, BaseModal, PaginationControl},
  data(){
    return {
      appMessage: {
        text: '',
        type: 'error',
      },
      visibleScheduleDowntime: false,
      visibleQuickBlock: false,
      visibleUnblockModal: false,
      visibleDeviceInfo: false,
      visibleCreateGroup: false,
      visibleRuleInfo: false,
      deviceBlockingOpen: true,
      groupBlockingOpen: false,
      scheduleOpen: false,
      deviceInfoEditMode: false,
      ruleEditMode: false,
      showAllSelectedDevices: false,
      groupsInactive: true,
      loading: false,
      createGroupScope: '',
      deviceSearchTerm: '',
      groupSearchTerm: '',
      newDeviceName: '',
      newGroupName: '',
      selectedDevices: [],
      selectedGroups: [],
      editSelectedDevices: [],
      editSelectedGroups: [],
      blockedDevicesToSelect: [],
      rules: [],
      rule: {
        frequency: '',
        start_time: '',
        end_time: '',
        day: '',
        date: '',
        id: '',
        hosts: [],
      },
      scheduleFrequencies: [{id: 0, name: "Daily"}, {id: 1, name: "Weekly"}, {id: 2, name: "Monthly"}, {id: 3, name: "Custom"}],
      scheduleFrequency: 0,
      scheduleDays: [{id: 0, name: "Sunday"}, {id: 1, name: "Monday"}, {id: 2, name: "Tuesday"}, {id: 3, name: "Wednesday"}, {id: 4, name: "Thursday"}, {id: 5, name: "Friday"}, {id: 6, name: "Saturday"},],
      scheduleDay: 0,
      daysOfMonth: Array.from({ length: 31 }, (_, i) => ({
        id: i,
        name: String(i + 1),
      })),
      dayOfMonth: 0,
      startTime: '10:00',
      endTime:   '10:30',
      editScheduleFrequency: 0,
      editScheduleDay: 0,
      editDayOfMonth: 0,
      editStartTime: '10:00',
      editEndTime: '10:30',
      editStartDate: '',
      editEndDate: '',
      blockDurations: [
        { value: 15, label: '15 min' },
        { value: 30, label: '30 min' },
        { value: 60,   label: '1 hr' },
        { value: 120,   label: '2 hr' }
      ],
      selectedDuration: null,
      startDate: '',
      endDate: '',
      device: {
        name: '',
        mac: '',
        ip: '',
        active: false,
        blocked: false,
      },
      group: {
        name: '',
        members: []
      },
      devices: [],
      // TODO: Remove fake groups
      groups: [
        {
          name: "phones",
          members: [
            "10.0.0.1",
            "10.0.0.2",
            "10.0.0.3"
          ]
        },
        {
          name: "kids",
          members: [
            "10.0.0.4",
            "10.0.0.5"
          ]
        },
        {
          name: "laptops",
          members: [
            "10.0.0.6",
            "10.0.0.7",
            "10.0.0.8"
          ]
        },
        {
          name: "tablets",
          members: [
            "10.0.0.9",
            "10.0.0.10"
          ]
        },
        {
          name: "windows-pcs",
          members: [
            "10.0.0.11",
            "10.0.0.12",
            "10.0.0.13"
          ]
        },
        {
          name: "chromebooks",
          members: [
            "10.0.0.14",
            "10.0.0.15"
          ]
        },
        {
          name: "android-devices",
          members: [
            "10.0.0.16",
            "10.0.0.17"
          ]
        },
        {
          name: "macbooks",
          members: [
            "10.0.0.18",
            "10.0.0.19"
          ]
        },
        {
          name: "desktops",
          members: [
            "10.0.0.20",
            "10.0.0.21"
          ]
        },
        {
          name: "mixed-use",
          members: [
            "10.0.0.22",
            "10.0.0.23",
            "10.0.0.24"
          ]
        },
        {
          name: "gaming",
          members: [
            "10.0.0.25",
            "10.0.0.26",
            "10.0.0.27"
          ]
        },
        {
          name: "office-devices",
          members: [
            "10.0.0.28",
            "10.0.0.29",
            "10.0.0.30"
          ]
        },
        {
          name: "guest-devices",
          members: [
            "10.0.0.31",
            "10.0.0.32"
          ]
        },
        {
          name: "streaming",
          members: [
            "10.0.0.33",
            "10.0.0.34"
          ]
        },
        {
          name: "development",
          members: [
            "10.0.0.35",
            "10.0.0.36",
            "10.0.0.37"
          ]
        },
        {
          name: "multimedia",
          members: [
            "10.0.0.38",
            "10.0.0.39",
            "10.0.0.40"
          ]
        },
        {
          name: "secure-zone",
          members: [
            "10.0.0.41",
            "10.0.0.42",
            "10.0.0.43"
          ]
        },
        {
          name: "media-devices",
          members: [
            "10.0.0.44",
            "10.0.0.45",
            "10.0.0.46"
          ]
        }
      ],
      currentDevicesPage: 1,
      itemsPerDevicesPage: 6,
      currentGroupPage: 1,
      itemsPerGroupsPage: 5,
      currentRulesPage: 1,
      itemsPerRulesPage: 6,
      dropdownOpen: false,
      deviceChecking: true,
    }
  },
  async mounted(){
    //console.clear();
    // Only 'device' users can access device manager functionality
    if (useLoginStore().getSubscriptionType === 'device') {
      this.deviceChecking = !useLoginStore().getDeviceOnline
      if (useLoginStore().getDeviceOnline) {
        await this.loadDeviceData()
      }
      this.startDeviceStatusWatcher()
    }
    // 'free' users handled by route guards/subscription service
  },
  computed: {
    isDeviceOnline() {
      return useLoginStore().getDeviceOnline
    },
    filteredDevices() {
      const term = this.deviceSearchTerm.trim().toLowerCase();
      if (!term) return this.devices;
      return this.devices.filter((device) => {
        const deviceName = this.getDeviceName(device);
        return deviceName && deviceName.toLowerCase().includes(term);
      });
    },
    devicesPageCount() {
      return Math.ceil(this.filteredDevices.length / this.itemsPerDevicesPage);
    },
    pagedDevices() {
      const startIndex = (this.currentDevicesPage - 1) * this.itemsPerDevicesPage;
      const endIndex = startIndex + this.itemsPerDevicesPage;
      return this.filteredDevices.slice(startIndex, endIndex);
    },
    rulesPageCount() {
      return Math.ceil(this.rules.length / this.itemsPerRulesPage);
    },
    pagedRules() {
      const start = (this.currentRulesPage - 1) * this.itemsPerRulesPage;
      return this.rules.slice(start, start + this.itemsPerRulesPage);
    },
    visibleDevicesPages() {
      const pages = [];
      const lower = Math.max(1, this.currentDevicesPage - 1);
      const upper = Math.min(this.devicesPageCount, this.currentDevicesPage + 1);
      for (let p = lower; p <= upper; p++) {
        pages.push(p);
      }
      return pages;
    },
    devicesToSelect() {
      return this.devices.map(device => ({
        id: device.mac,
        name: this.getDeviceName(device)
      }));
    },
    groupsToSelect() {
      return this.groups.map((group, idx) => ({
        id: idx,
        name: group.name
      }));
    },
    IPsInSelectedGroups() {
      return new Set(this.selectedGroups.flatMap((group_idx) => this.groups[group_idx].members))
    },
    devicesNotInSelectedGroupsToSelect() {
      return this.devices.filter(device => !this.IPsInSelectedGroups.has(device.mac)).map(device => ({
        id: device.mac,
        name: this.getDeviceName(device)
      }));
    },
    combinedDevices() {
      const allDevices = []
      this.selectedDevices.forEach(ip => {
        allDevices.push(ip)
      })
      Array.from(this.IPsInSelectedGroups).forEach(mac => {
        allDevices.push(mac)
      })
      const allDevicesSet = new Set(allDevices)
      return Array.from(allDevicesSet)
    },
    macToDevice() {
      return this.devices.reduce((map, device) => {
        map[device.mac] = device;
        return map;
      }, {});
    },
    ipToDevice() {
      return this.devices.reduce((map, device) => {
        map[device.ip] = device;
        return map;
      }, {});
    },
    groupNameToIndex() {
      return this.groups.reduce((map, group, idx) => {
        map[group.name] = idx;
        return map;
      }, {});
    },
    filteredGroups() {
      const term = this.groupSearchTerm.trim().toLowerCase();
      if (!term) return this.groups;
      return this.groups.filter((g) =>
          g.name.toLowerCase().includes(term)
      );
    },
    groupPageCount() {
       return Math.ceil(this.filteredGroups.length / this.itemsPerGroupsPage);
     },
    pagedGroups() {
       const startIndex = (this.currentGroupPage - 1) * this.itemsPerGroupsPage;
       const endIndex = startIndex + this.itemsPerGroupsPage;
       return this.filteredGroups.slice(startIndex, endIndex);
     },
     visibleGroupPages() {
       const pages = [];
       const lower = Math.max(1, this.currentGroupPage - 1);
       const upper = Math.min(this.groupPageCount, this.currentGroupPage + 1);
       for (let p = lower; p <= upper; p++) {
           pages.push(p);
         }
       return pages;
     },
  },
  watch: {
    isDeviceOnline(newValue) {
      if (newValue && this.deviceChecking) {
        this.deviceChecking = false
        this.loadDeviceData()
      } else if (!newValue) {
        this.deviceChecking = true
      }
    }
  },
  methods: {

    getDeviceName(device) {
      if (!device) return 'Unknown Device';
      return device.displayName || device.name || 'Unknown Device';
    },
    getDeviceByHostIdentifier(identifier) {
      // Try MAC first
      let device = this.macToDevice[identifier];
      if (device) return device;
      // Fall back to IP (for backward compatibility with API responses)
      device = this.ipToDevice[identifier];
      if (device) return device;
      return null;
    },
    async loadDeviceData() {
      this.loading = true
      try {
        await this.getDevices()
        await this.getScheduledRules()
      } catch (e) {
        console.error('Error loading device data:', e)
      }
      this.loading = false
    },
    startDeviceStatusWatcher() {
      const checkInterval = setInterval(() => {
        if (useLoginStore().getSubscriptionType === 'device' && useLoginStore().getDeviceOnline && this.deviceChecking) {
          this.deviceChecking = false
          this.loadDeviceData()
          clearInterval(checkInterval)
        }
      }, 1000)
      
      setTimeout(() => {
        clearInterval(checkInterval)
        if (this.deviceChecking) {
          this.deviceChecking = false
        }
      }, 30000)
    },
    onDropdownOpen() {
      this.dropdownOpen = true;
    },
    onDropdownClose() {
      this.dropdownOpen = false;
    },
    showError(e){
      this.appMessage.text = 'Error: ' + e
      this.appMessage.type = 'error'
      this.$refs.messageModal.open()
    },
    goToRulesPage(page) {
      if (page < 1) page = 1;
      if (page > this.rulesPageCount) page = this.rulesPageCount;
      this.currentRulesPage = page;
    },
    async getDevices() {
      try {
        const response = await deviceAPIService.getDevices(this.$router, useLoginStore());
        // ensure every device has a boolean blocked flag
        this.devices = response.devices.map(d => ({ ...d, blocked: false }));
        await this.getBlockedStatus();
      } catch (e) {
        this.showError(e);
      }
    },
    async getBlockedStatus() {
      try {
        const response = await deviceAPIService.getDeviceBlockStatus(this.$router, useLoginStore());
        // reset everything first
        this.devices.forEach(device => device.blocked = false);

        // then mark blocked devices
        response.entries.forEach(entry => {
          entry.ips.forEach(ip => {
            const device = this.ipToDevice[ip];
            if (device) device.blocked = true;
          });
        });
      } catch (e) {
        this.showError(e);
      }
    },
    async getScheduledRules() {
      try {
        this.rules = await deviceAPIService.getScheduleRules(this.$router, useLoginStore());
      }
      catch (e) {
        this.showError(e)
      }
    },
    militaryToAmPm(milStr) {
      milStr = String(milStr)
      let [hStr, mStr] = milStr.split(':');
      let h = parseInt(hStr, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      return `${h}:${mStr} ${ampm}`;
    },
    convertToMonth(num) {
      return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][num - 1]
    },
    parseDate(dateStr) {
      const date_lst = dateStr.split('-').map(item => Number(item))

      return this.convertToMonth(date_lst[1]).concat(" ", date_lst[2], ",", " ", date_lst[0])
    },
    computeBlockedDevicesToSelect() {
      this.blockedDevicesToSelect = this.devices
          .filter(d => d.blocked)
          .map(d => ({ id: d.ip, name: this.getDeviceName(d) }));
    },
    openModal(modalName) {
      this[modalName] = true
    },
    closeModal(modalName) {
      this[modalName] = false
    },
    closeScheduleDowntimeModal() {
      this.selectedDevices = [];
      this.selectedGroups = [];
      this.scheduleFrequency = 0;
      this.scheduleDay = 0;
      this.dayOfMonth = 0;
      this.startTime = '10:00';
      this.endTime = '10:30';
      this.startDate = '';
      this.endDate = '';
      this.closeModal('visibleScheduleDowntime');
    },
    async scheduleDowntime() {
      if (this.combinedDevices.length === 0){
        this.appMessage.text = "No Devices Selected";
        this.appMessage.type = "error";
        this.$refs.messageModal.open();
      }
      else {
        this.loading=true;
        try {
          for (const mac of this.combinedDevices) {
            const device = this.macToDevice[mac];
            const ip = device ? device.ip : null;
            if (!ip) continue;

            if (this.scheduleFrequency === 0) {
              await deviceAPIService.postScheduleRules({
                ip: ip,
                frequency: "daily",
                start_time: this.startTime,
                end_time: this.endTime
              }, this.$router, useLoginStore())
            } else if (this.scheduleFrequency === 1) {
              await deviceAPIService.postScheduleRules({
                ip: ip,
                frequency: "weekly",
                day: this.scheduleDay,
                start_time: this.startTime,
                end_time: this.endTime
              }, this.$router, useLoginStore())
            } else if (this.scheduleFrequency === 2) {
              await deviceAPIService.postScheduleRules({
                ip: ip,
                frequency: "monthly",
                date: this.dayOfMonth + 1,
                start_time: this.startTime,
                end_time: this.endTime
              }, this.$router, useLoginStore())
            } else if (this.scheduleFrequency === 3) {
              await deviceAPIService.postScheduleRules({
                ip: ip,
                frequency: "custom",
                start_time: this.startDate.concat("T", this.startTime),
                end_time: this.endDate.concat("T", this.endTime)
              }, this.$router, useLoginStore())
            }
          }
          await this.getScheduledRules()
          this.closeScheduleDowntimeModal()
          this.loading=false;
          this.appMessage.text = 'Successfully Scheduled Downtime!'
          this.appMessage.type = 'success'
          this.$refs.messageModal.open()
        }
        catch (e) {
          this.loading=false;
          this.showError(e)
        }
      }
    },
    openDeviceInfoModal(device) {
      this.device = device; // preserve live reference
      this.newDeviceName = this.getDeviceName(device); // for renaming
      this.openModal("visibleDeviceInfo");
    },
    closeDeviceInfoModal() {
      this.deviceInfoEditMode=false;
      this.newDeviceName = "";
      this.closeModal("visibleDeviceInfo");
    },
    openCreateGroupModal(scope) {
      this.createGroupScope = scope
      this.openModal('visibleCreateGroup');
    },
    closeCreateGroupModal() {
      this.createGroupScope='';
      this.selectedDevices = [];
      this.newGroupName = '';
      this.group.name = '';
      this.group.members = [];
      this.closeModal('visibleCreateGroup');
    },
    submitCreateGroupModal() {
      if (this.createGroupScope === 'Add') {
        const lowerCase = this.newGroupName.toLowerCase();
        if (this.groups.some(g => g.name.toLowerCase() === lowerCase)) {
          this.appMessage.text = "This Group Already Exists";
          this.appMessage.type = "error";
          this.$refs.messageModal.open();
        } else if (this.newGroupName === "") {
          this.appMessage.text = "Group Needs a Name";
          this.appMessage.type = "error";
          this.$refs.messageModal.open();
        }else if (this.selectedDevices.length === 0) {
          this.appMessage.text = "No Devices Selected";
          this.appMessage.type = "error";
          this.$refs.messageModal.open();
        } else {
          console.log("made group:", this.newGroupName, "with members:", this.selectedDevices);
          this.groups.push({name: this.newGroupName, members: this.selectedDevices})
          // TODO: stub for API call to make a group
          this.closeCreateGroupModal();
        }
      }
      else if (this.createGroupScope === 'Block Internet') {
        this.createGroupScope = '';
        this.selectedDevices = [];
        this.newGroupName = '';
        this.closeModal('visibleCreateGroup');
        this.selectedGroups = [this.groupNameToIndex[this.group.name]];
        this.openModal('visibleQuickBlock');
      }
      else if (this.createGroupScope ==='Confirm') {
       if (this.group.members.length === 0) {
          this.appMessage.text = "No Devices Selected";
          this.appMessage.type = "error";
          this.$refs.messageModal.open();
        }
       else {
         if (this.newGroupName === "") {
          this.newGroupName = this.group.name
         }
          console.log("changed group:", this.group.name, "to", this.newGroupName, "with members:", this.group.members);
          // TODO: stub for API call to edit group
          this.closeCreateGroupModal();
        }
      }
    },
    openBlockGroupMode(group){
      this.group.name = group.name
      this.group.members = group.members
      this.openCreateGroupModal('Block Internet')
    },
    editGroup() {
      this.createGroupScope = 'Confirm'
    },
    toEditMode() {
      this.deviceInfoEditMode = true;
    },
    toggleDeviceBlocking() {
      this.deviceBlockingOpen = !this.deviceBlockingOpen;
    },
    toggleGroupBlocking() {
      this.groupBlockingOpen = !this.groupBlockingOpen;
    },
    toggleSchedule() {
      this.scheduleOpen = !this.scheduleOpen;
    },
    displayStatus(blocked) {
      return blocked ? "Blocked" : "Unblocked";
    },
    filter(){

    },
    goToDevicePage(page) {
      if (page < 1) page = 1;
      if (page > this.devicesPageCount) page = this.devicesPageCount;
      this.currentDevicesPage = page;
    },
    goToGroupPage(page) {
      if (page < 1) page = 1;
      if (page > this.groupPageCount) page = this.groupPageCount;
      this.currentGroupPage = page;
    },
    async deviceInfoSubmit() {
      if (this.deviceInfoEditMode) {
        // ensure they don't accidentally rename their device to nothing
        if (this.newDeviceName === "") {
          this.deviceInfoEditMode = false;
        }
        else {
          this.loading=true;
          try {
            deviceAPIService.putRenameDevice(this.device.mac, {name: this.newDeviceName}, this.$router, useLoginStore())
            await this.getDevices()
            // Update both possible name fields
            this.device.name = this.newDeviceName;
            this.device.displayName = this.newDeviceName;
            this.newDeviceName = '';
            this.deviceInfoEditMode = false;
            this.loading=false;
          }
          catch (e) {
            this.loading=false;
            this.showError(e)
          }
        }
      }
      else if (this.device.blocked) {
        this.closeDeviceInfoModal();
        this.selectedDevices = [this.device.mac];
        this.computeBlockedDevicesToSelect();
        this.openModal('visibleUnblockModal');
      }
      else {
        this.closeDeviceInfoModal();
        this.selectedDevices = [this.device.mac]
        this.openModal('visibleQuickBlock');
      }
    },
    closeQuickBlockModal() {
      this.closeModal('visibleQuickBlock');
      this.selectedDuration = null;
      this.selectedGroups = [];
      this.selectedDevices = [];
      // leave this.device alone so its name/ip stay visible
    },
    async submitQuickBlock() {
      const duration = this.selectedDuration;

      if (duration === null) {
        this.appMessage.text = "No Duration Selected";
        this.appMessage.type = "error";
        this.$refs.messageModal.open();
        return;
      }

      if (this.combinedDevices.length === 0) {
        this.appMessage.text = "No Devices Selected";
        this.appMessage.type = "error";
        this.$refs.messageModal.open();
        return;
      }

      this.loading = true;

      try {
        // Convert MAC addresses to IP addresses for the API
        const ips = this.combinedDevices.map(mac => {
          const device = this.macToDevice[mac];
          return device ? device.ip : null;
        }).filter(ip => ip !== null);

        const payload = {
          duration: duration,
          ips: ips
        };

        await deviceAPIService.postBlockInternetDevice(
            payload,
            this.$router,
            useLoginStore()
        );

        await this.getDevices();
        await this.getBlockedStatus();
        const count = this.combinedDevices.length;
        const updated = this.devices.find(d => d.mac === this.device.mac);
        if (updated) {
          this.device = updated;
        }
        this.closeQuickBlockModal();
        this.refreshDeviceModal()
        this.appMessage.text = `Blocked ${count} device(s)`;
        this.appMessage.type = "success";
        this.$refs.messageModal.open();
      } catch (e) {
        this.showError(e);
      } finally {
        this.loading = false;
      }
    },
    refreshDeviceModal() {
      const updated = this.devices.find(d => d.mac === this.device.mac);
      if (updated) {
        this.device = updated;
      }
    },
    closeUnblockModal() {
      this.closeModal('visibleUnblockModal');
      this.selectedDevices = [];
      this.blockedDevicesToSelect = [];
    },
    async submitUnblock() {
      if (this.selectedDevices.length === 0) {
        this.appMessage.text = "No Devices Selected";
        this.appMessage.type = "error";
        this.$refs.messageModal.open();
        return;
      }

      this.loading = true;

      try {
        // Convert MAC addresses to IP addresses for the API
        const ips = this.selectedDevices.map(mac => {
          const device = this.macToDevice[mac];
          return device ? device.ip : null;
        }).filter(ip => ip !== null);

        await deviceAPIService.postUnblockInternetDevice(
            { ips: ips },
            this.$router,
            useLoginStore()
        );

        await this.getDevices();
        await this.getBlockedStatus();
        const updated = this.devices.find(d => d.mac === this.device.mac);
        if (updated) {
          this.device = updated;
        }
        this.closeUnblockModal();
        this.refreshDeviceModal()
        this.appMessage.text = "Devices unblocked";
        this.appMessage.type = "success";
        this.$refs.messageModal.open();
      } catch (e) {
        this.showError(e);
      } finally {
        this.loading = false;
      }
    },
    openRuleInfo(rule) {
      this.rule.frequency = rule.frequency;
      this.rule.start_time = rule.start_time;
      this.rule.end_time = rule.end_time;
      this.rule.id = rule.id;
      this.rule.hosts = rule.hosts;
      if (rule.frequency === 'weekly'){
        this.rule.day = rule.day
      }
      if (rule.frequency === 'monthly') {
        this.rule.date = rule.date
      }
      this.openModal('visibleRuleInfo')
    },
    closeRuleInfo() {
      this.rule.frequency = '';
      this.rule.start_time = '';
      this.rule.end_time = '';
      this.rule.day = '';
      this.rule.date = '';
      this.rule.id = '';
      this.rule.hosts = [];
      this.ruleEditMode = false;
      this.closeModal('visibleRuleInfo')
    },
    editSchedule() {
      this.ruleEditMode = true;
      
      // Populate edit form with current rule data
      this.editSelectedDevices = [...this.rule.hosts];
      
      // Set frequency
      if (this.rule.frequency === 'daily') {
        this.editScheduleFrequency = 0;
      } else if (this.rule.frequency === 'weekly') {
        this.editScheduleFrequency = 1;
        this.editScheduleDay = this.rule.day;
      } else if (this.rule.frequency === 'monthly') {
        this.editScheduleFrequency = 2;
        this.editDayOfMonth = this.rule.date - 1; // Convert to 0-based index
      } else if (this.rule.frequency === 'custom') {
        this.editScheduleFrequency = 3;
        this.editStartDate = this.rule.start_time.split('T')[0];
        this.editEndDate = this.rule.end_time.split('T')[0];
        this.editStartTime = this.rule.start_time.split('T')[1];
        this.editEndTime = this.rule.end_time.split('T')[1];
      }
      
      // Set times for daily, weekly, monthly
      if (this.rule.frequency !== 'custom') {
        this.editStartTime = this.rule.start_time;
        this.editEndTime = this.rule.end_time;
      }
    },
    cancelScheduleEdit() {
      this.ruleEditMode = false;
      // Reset edit form data
      this.editSelectedDevices = [];
      this.editSelectedGroups = [];
      this.editScheduleFrequency = 0;
      this.editScheduleDay = 0;
      this.editDayOfMonth = 0;
      this.editStartTime = '10:00';
      this.editEndTime = '10:30';
      this.editStartDate = '';
      this.editEndDate = '';
    },
    async saveScheduleChanges() {
      if (this.editSelectedDevices.length === 0) {
        this.appMessage.text = "No Devices Selected";
        this.appMessage.type = "error";
        this.$refs.messageModal.open();
        return;
      }

      this.loading = true;

      try {
        // Convert MAC to IP for API
        const mac = this.editSelectedDevices[0];
        const device = this.macToDevice[mac];
        const ip = device ? device.ip : null;

        if (!ip) {
          this.appMessage.text = "Device IP not found";
          this.appMessage.type = "error";
          this.$refs.messageModal.open();
          this.loading = false;
          return;
        }

        // Build the data object based on frequency
        let ruleData = {};

        if (this.editScheduleFrequency === 0) {
          // Daily
          ruleData = {
            ip: ip,
            frequency: "daily",
            start_time: this.editStartTime,
            end_time: this.editEndTime
          };
        } else if (this.editScheduleFrequency === 1) {
          // Weekly
          ruleData = {
            ip: ip,
            frequency: "weekly",
            day: this.editScheduleDay,
            start_time: this.editStartTime,
            end_time: this.editEndTime
          };
        } else if (this.editScheduleFrequency === 2) {
          // Monthly
          ruleData = {
            ip: ip,
            frequency: "monthly",
            date: this.editDayOfMonth + 1, // Convert back to 1-based
            start_time: this.editStartTime,
            end_time: this.editEndTime
          };
        } else if (this.editScheduleFrequency === 3) {
          // Custom
          ruleData = {
            ip: ip,
            frequency: "custom",
            start_time: this.editStartDate.concat("T", this.editStartTime),
            end_time: this.editEndDate.concat("T", this.editEndTime)
          };
        }

        await deviceAPIService.putScheduleRules(this.rule.id, ruleData, this.$router, useLoginStore());
        
        // Refresh the rules list
        await this.getScheduledRules();
        
        // Close the modal and reset edit mode
        this.ruleEditMode = false;
        this.closeRuleInfo();
        
        this.appMessage.text = 'Successfully Updated Schedule!';
        this.appMessage.type = 'success';
        this.$refs.messageModal.open();
      } catch (e) {
        this.showError(e);
      } finally {
        this.loading = false;
      }
    },
    deleteRuleButton(rule) {
      this.rule.frequency = rule.frequency;
      this.rule.start_time = rule.start_time;
      this.rule.end_time = rule.end_time;
      this.rule.id = rule.id;
      this.rule.hosts = rule.hosts;
      if (rule.frequency === 'weekly'){
        this.rule.day = rule.day;
      }
      if (rule.frequency === 'monthly') {
        this.rule.date = rule.date;
      }
      this.deleteRule();
    },
    async deleteRule() {
      this.loading=true;
      try {
        await deviceAPIService.deleteScheduleRules(this.rule.id, this.$router, useLoginStore());
        await this.getScheduledRules();
        this.closeRuleInfo();
        this.loading=false;
      }
      catch (e) {
        this.showError(e)
        this.loading=false;
      }
    },
    async duplicateRule(rule) {
      this.loading = true;

      try {
        // Create a duplicate for each device in the rule's hosts
        for (const mac of rule.hosts) {
          // Convert MAC to IP for API
          const device = this.macToDevice[mac];
          const ip = device ? device.ip : null;
          if (!ip) continue;

          let ruleData = {};

          if (rule.frequency === 'daily') {
            ruleData = {
              ip: ip,
              frequency: "daily",
              start_time: rule.start_time,
              end_time: rule.end_time
            };
          } else if (rule.frequency === 'weekly') {
            ruleData = {
              ip: ip,
              frequency: "weekly",
              day: rule.day,
              start_time: rule.start_time,
              end_time: rule.end_time
            };
          } else if (rule.frequency === 'monthly') {
            ruleData = {
              ip: ip,
              frequency: "monthly",
              date: rule.date,
              start_time: rule.start_time,
              end_time: rule.end_time
            };
          } else if (rule.frequency === 'custom') {
            ruleData = {
              ip: ip,
              frequency: "custom",
              start_time: rule.start_time,
              end_time: rule.end_time
            };
          }

          await deviceAPIService.postScheduleRules(ruleData, this.$router, useLoginStore());
        }
        
        // Refresh the rules list
        await this.getScheduledRules();
        
        this.appMessage.text = 'Successfully Duplicated Schedule!';
        this.appMessage.type = 'success';
        this.$refs.messageModal.open();
      } catch (e) {
        this.showError(e);
      } finally {
        this.loading = false;
      }
    },
  },
}
</script>

<style scoped>
.dropdownSpacer{
  height: 100px;
}

/* Clickable device row styles */
.clickable-device-row {
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
}

.clickable-device-row:hover {
  background: var(--color-border-input) !important;
  transform: translateX(2px);
}

.clickable-device-row:active {
  transform: translateX(0px);
}

.banner-toggle-btn {
  background: none;
  border: none;
  color: var(--text-primary);
  font-size: 1.2rem;
  font-weight: 200;
  cursor: pointer;
  width: 1.8rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-toggle-btn:focus {
  outline: none;
}

.banner-toggle-btn:hover {
  opacity: 0.75;
}

.button‐group {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-top: .5rem;
  margin-bottom: 1.5rem;
}

/* —— “Schedule Downtime” uses the success‐green variables —— */
.btn‐schedule {
  background-color: var(--color-secondary-button);
  border: 1px solid var(--color-secondary-button-border);
  color: var(--text-primary);
  padding: 0.35rem .5rem;
  font-size: 1rem;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: opacity 0.2s;
}
.btn‐schedule:hover {
  opacity: 0.9;
}

.pageButton:hover {
  opacity: 0.9;
}

/* four “columns” in each row: icon / name / status / info‐icon */
.device‐column {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: var(--text-primary);
}

/* adjust individual columns if needed */
.device‐icon {
  flex: 0 0 2rem;       /* fixed 2rem for icon */
  justify-content: flex-start;
  font-size: 1.3rem;
}

.device‐name {
  flex: 2;
  justify-content: flex-start;
  font-size: .9rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device‐status {
  flex: 1;
  font-size: .9rem;
  justify-content: flex-start;
  padding-left: 1rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.device‐info‐icon {
  flex: 0 0 2rem;       /* fixed width for info icon */
  justify-content: flex-end;
  font-size: 1.3rem;
  color: var(--color-secondary-button);
}

.add-group-button {
  display: flex;
  justify-content: left;
  margin-left: 1rem;
  margin-top: 1rem;
  margin-bottom: 1.5rem;
}

.text-box-entry[type="time"] {
  border: none;
  background: transparent;
  box-shadow: none;
  -webkit-appearance: none;
  appearance: none;
  padding: 0;
  width: 100%;
}

.text-box-entry[type="time"]::-webkit-calendar-picker-indicator,
.text-box-entry[type="time"]::-webkit-inner-spin-button {
  display: none;
}

.text-box-entry[type="date"] {
  border: none;
  background: transparent;
  box-shadow: none;
  -webkit-appearance: none;
  appearance: none;
  padding: 0;
  width: 100%;
}

.text-box-entry[type="date"]::-webkit-calendar-picker-indicator,
.text-box-entry[type="date"]::-webkit-inner-spin-button {
  display: none;
}

.text-box-title {
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  margin: 0 0.5rem;
}

.IP {
  margin-right: 2.55rem;
}

.MAC {
  margin-right: 1.2rem;
}

.device-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-background-input);
  border: 1px solid var(--color-border-input);
  font-weight: 500;
  font-size: 0.9rem;
  border-radius: 0.375rem;
  padding: 0.5rem .2rem;
  margin-bottom: 1rem;
  font-family: 'Poppins', 'Inter', sans-serif;
}

.rename {
  font-family: 'Poppins', 'Inter', sans-serif;
  margin-right: 0;
  margin-left: auto;
  font-size: .9rem;
  padding: .15rem .4rem;
  font-weight: 400;
  cursor: pointer;
  transition: opacity 0.2s;
}

.group-name {
  color: var(--text-primary);
  margin-left: 1rem;
  font-family: 'Poppins', 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0;
}

.selected-devices-list {
  max-height: 35vw;       /* or whatever fits inside your modal */
  overflow-y: auto;
  padding-right: 8px;      /* leave room for scrollbar */
  margin-bottom: 1rem;
}

/* optional: some spacing between each device name */
.selected-device-item {
  padding: 4px 0;
  font-family: "Helvetica Neue", Helvetica, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", "微软雅黑", Arial, sans-serif;
  font-size: .8rem;
  white-space: nowrap;     /* prevent wrapping if you’d rather keep each name on one line */
}

/* if you want a subtle separator between items: */
.selected-device-item:not(:last-child) {
  border-bottom: 1px solid  var(--color-divider);
}

.button-bar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

/* base style for each toggle button */
.block-btn {
  padding: 0.4rem 0.75rem;
  border: 1px solid var(--color-secondary-button);
  background-color: transparent;
  color: var(--text-primary);
  border-radius: 0.25rem;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s, color 0.2s;
}

.block-btn:hover {
  opacity: 0.85;
}

/* when selected, show solid primary‐blue background and white text */
.block-btn-active {
  background-color: var(--color-secondary-button);
  color: var(--text-primary);
  border-color: var(--color-secondary-button-border);
}

.frequency {
  text-align: left;
}

.times {
  text-align: right;
}

.icon-group {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.9rem; /* adjust spacing as desired */
  color: var(--text-primary);
  flex: 0 0 auto; /* don’t stretch */
}

.icon-trash {
  color: var(--color-red-button);
  cursor: pointer;
  font-size: 1.2rem;
}

.icon-duplicate {
  color: var(--color-primary-button);
  cursor: pointer;
  font-size: 1.2rem;
}

.icon-info {
  color: var(--color-secondary-button);
  cursor: pointer;
  font-size: 1.3rem;
}

.schedule-device {
  text-align: left;
  flex: 1 1 auto;          /* allow flex‐shrink */
  min-width: 0;            /* so it actually shrinks */
  max-width: 19ch;         /* roughly 15 characters wide */
  overflow: hidden;
  white-space: nowrap;
}

.info-icon-blocked {
  color: var(--color-red-button); /* or just use 'red' */
  cursor: pointer;
}

.info-icon-unblocked {
  color: var(--color-secondary-button);
  cursor: pointer;
}

.info-icon-blocked:hover,
.info-icon-unblocked:hover {
  opacity: 0.8;
}

/* Device Status Banner */
.device-status-banner {
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
}

.device-status-banner.offline {
  background: linear-gradient(135deg, #FF9800, #F57C00);
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

.status-content {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-icon {
  font-size: 1rem;
}

.status-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Dark mode adjustments for status banners */
[data-theme="dark"] .device-status-banner {
  background: linear-gradient(135deg, #2E7D32, #1B5E20);
}

[data-theme="dark"] .device-status-banner.offline {
  background: linear-gradient(135deg, #E65100, #BF360C);
}

/* Disabled button styles */
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

</style>
